import { Box, Typography } from '@material-ui/core'
import {
  AutocompleteInput,
  BooleanInput,
  FormDataConsumer,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  TextInput,
} from 'react-admin'

import { BundleChoices, ELDTypeChoices, SafetyEmailGradeChoices } from '../../helpers/constants'
import { ProductSubscriptionBundleName } from '../../models/subscription'

const validateRequired = [required()]

const driverTextRenderer = (choice: any) =>
  choice && `${choice.first_name}, ${choice.last_name} (${choice.email})`

const bundlesWithAutomatedOnboarding = [
  ProductSubscriptionBundleName.FLEX_STARTER,
  ProductSubscriptionBundleName.CREDIT_ONLY,
  ProductSubscriptionBundleName.PREMIUM,
]
/**
 * Add form componets for scenario fields here.
 *
 * Each scenario should have:
 * 1. Key corresponding to the class name in cloudtrucks/console/qa/scenarios.py
 * 2. Value returning a single react component. Typically composed of multiple children.
 *    `source` should correspond to input field names on the referenced serializer class in scenarios.py
 */
export const scenarioFields: any = {
  BasicTeamScenario: (
    <Box display="flex" flexDirection="column">
      <Box flex={3} marginY={2}>
        <NumberInput
          source="num_admin_driver"
          label="Number admin drivers"
          helperText="Users that are both admin and driver."
          min={0}
          step={1}
          validate={validateRequired}
        />
        <NumberInput
          source="num_admin_only"
          label="Number admins"
          helperText="Users that are admins but not drivers."
          min={0}
          step={1}
          validate={validateRequired}
        />
        <NumberInput
          source="num_driver_only"
          label="Number drivers"
          helperText="Users that are drivers but not admins."
          min={0}
          step={1}
          validate={validateRequired}
        />
      </Box>
      <Box flex={1} marginY={2}>
        <SelectInput
          source="product_subscription"
          label="Bundle"
          helperText="This team will be subscribed to the following bundle with date_agreement_signed=now."
          choices={BundleChoices}
        />
      </Box>
      <Box flex={1} marginY={2}>
        <BooleanInput
          source="is_automation_team"
          label="Automate login?"
          helperText="All members in this team will have a deterministic login code."
        />
      </Box>

      <Box flex={1} marginY={2}>
        <FormDataConsumer>
          {({ formData }) =>
            bundlesWithAutomatedOnboarding.includes(formData.product_subscription) && (
              <BooleanInput
                source="auto_complete_onboarding"
                label="Automate Onboarding?"
                helperText="This account will be created with all items on the onboarding checklist completed."
              />
            )
          }
        </FormDataConsumer>
      </Box>

      <Box flex={1} marginY={2}>
        <FormDataConsumer>
          {({ formData }) =>
            formData.product_subscription === ProductSubscriptionBundleName.FLEX_STARTER && (
              <BooleanInput
                source="generate_jobs_with_load_stop_progress"
                label="Generate jobs with load stop progress?"
                helperText="This will create few jobs with load stop progress data."
              />
            )
          }
        </FormDataConsumer>
      </Box>
    </Box>
  ),
  DataformRunStagingPipeline: (
    <Box display="flex">
      <TextInput
        source="run_id"
        label="Run ID"
        helperText="The run id of a previously started pipeline. If empty, submitting will trigger a new pipeline run."
      />
    </Box>
  ),
  SuggestedScheduleNotification: (
    <Box>
      <Box>
        <Typography variant="caption">
          Please ensure that the appropriate waffle flags are set to allow notification to proceed:
          schedules_suggest_next_load and schedules_suggest_next_load_notification. The driver must also have
          an ELD account, you can create a mock ELD account in CT Admin.
        </Typography>
        <Typography variant="caption">
          The notification will be sent once the schedule finishes generation, only if the schedule finds a
          load.
        </Typography>
      </Box>
      <Box display="flex">
        <ReferenceInput
          source="id"
          reference="users"
          name="user_id"
          perPage={25}
          filterToQuery={(searchText) => ({ search: searchText })}
          label="Driver"
          validate={validateRequired}
        >
          <AutocompleteInput optionText={driverTextRenderer} optionValue="id" fullWidth={true} />
        </ReferenceInput>
      </Box>
    </Box>
  ),
  TriggerSuggestedScheduleNotification: (
    <Box>
      <Box>
        <Typography>Trigger a notification for the latest OPTIMIZER Schedule for a user.</Typography>
      </Box>
      <Box display="flex">
        <ReferenceInput
          source="id"
          reference="users"
          name="user_id"
          perPage={25}
          filterToQuery={(searchText) => ({ search: searchText })}
          label="Driver"
          validate={validateRequired}
        >
          <AutocompleteInput optionText={driverTextRenderer} optionValue="id" fullWidth={true} />
        </ReferenceInput>
      </Box>
      <Box>
        <Typography variant="caption">
          Please ensure that the appropriate waffle flags are set to allow notification to proceed:
          schedules_suggest_next_load_notification.
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption">
          The notification will be sent based on the last optimizer generated schedule which was shown to the
          user (and will not change how the schedule appears in the app).
        </Typography>
      </Box>
    </Box>
  ),
  SafetyScoreEmail: (
    <Box display="flex" flexDirection="column" sx={{ width: '50%' }}>
      <Box>
        <ReferenceInput
          source="id"
          reference="users"
          name="user"
          perPage={25}
          filterToQuery={(searchText) => ({ search: searchText })}
          label="Driver"
          validate={validateRequired}
        >
          <AutocompleteInput optionText={driverTextRenderer} optionValue="id" fullWidth={true} />
        </ReferenceInput>
      </Box>
      <Box>
        <SelectInput
          source="eld_provider_type"
          label="ELD provider type"
          helperText="Name of the eld provider."
          choices={ELDTypeChoices}
        />
      </Box>
      <Box>
        <NumberInput
          source="version"
          label="Safety Email Version"
          helperText="Version of safety email to send."
          min={1}
          step={1}
          initialValue={1}
          validate={validateRequired}
        />
      </Box>
      <Box>
        <SelectInput
          source="hos_weekly_grade"
          label="HOS Weekly Grade"
          helperText="The score grade, e.g. Excellent, Good., (version 1)"
          choices={SafetyEmailGradeChoices}
        />
      </Box>
      <Box>
        <NumberInput
          source="hos_weekly_severity_score"
          label="HOS Weekly Severity Score"
          helperText="Numeric safety score. (version 1)"
          min={0}
        />
      </Box>
      <Box>
        <SelectInput
          source="safety_weekly_grade"
          label="Safety Weekly Grade"
          helperText="The score grade, e.g. Excellent, Good. (version 2)"
          choices={SafetyEmailGradeChoices}
        />
      </Box>
      <Box px={10}>
        <Typography>Version 1+ fields:</Typography>

        <NumberInput
          source="hos_driven_flag_days_7d"
          label="# driving hours incidents"
          helperText="11+ driving hours"
          min={0}
          max={7}
          step={1}
        />
        <NumberInput
          source="hos_active_flag_days_7d"
          label="# active hours incidents"
          helperText="14+ active hours"
          min={0}
          max={7}
          step={1}
        />
        <NumberInput
          source="hos_ym_flag_days_7d"
          label="# yard move incidents"
          helperText="1+ yard move hours"
          min={0}
          max={7}
          step={1}
        />
        <NumberInput
          source="hos_pc_flag_days_7d"
          label="# personal conveyance incidents"
          helperText="2+ personal conveyance hours"
          min={0}
          max={7}
          step={1}
        />
        <Typography>Version 2+ fields:</Typography>
        <NumberInput source="num_crash_7d" label="# crashes" helperText="Crash" min={0} step={1} />
        <NumberInput
          source="num_braking_7d"
          label="# harsh breaking"
          helperText="Harsh breaking"
          min={0}
          step={1}
        />
        <NumberInput
          source="num_harshTurn_7d"
          label="# harsh turn"
          helperText="Harsh Turn"
          min={0}
          step={1}
        />
        <NumberInput
          source="num_acceleration_7d"
          label="# harsh acceleration"
          helperText="Harsh Acceleration"
          min={0}
          step={1}
        />
        <Typography>Version 3+ fields:</Typography>
        <Box>
          <NumberInput source="num_approved_jobs_7d" label="num_approved_jobs_7d" min={0} step={1} />
          <NumberInput source="num_completed_jobs_7d" label="num_completed_jobs_7d" min={0} step={1} />
          <NumberInput source="num_driver_canc_lt_0h_7d" label="num_driver_canc_lt_0h_7d" min={0} step={1} />
          <NumberInput source="num_driver_canc_0_8h_7d" label="num_driver_canc_0_8h_7d" min={0} step={1} />
          <NumberInput source="num_driver_canc_8_48h_7d" label="num_driver_canc_8_48h_7d" min={0} step={1} />
          <NumberInput
            source="num_driver_canc_lt_48h_7d"
            label="num_driver_canc_lt_48h_7d"
            min={0}
            step={1}
          />
        </Box>
      </Box>
      <Box pt={3}>
        <Typography variant="caption">
          Note that we do not dynamically check required fields per version. If you see a success = "false"
          response, please check that required fields for the version are filled out.
        </Typography>
      </Box>
    </Box>
  ),
  PushRecommendation: (
    <Box>
      <Box>
        <Typography>Trigger the Push-Rex push recommendation service for driver.</Typography>
      </Box>
      <Box display="flex">
        <ReferenceInput
          source="id"
          reference="users"
          name="user_id"
          perPage={25}
          filterToQuery={(searchText) => ({ search: searchText })}
          label="Driver"
          validate={validateRequired}
        >
          <AutocompleteInput optionText={driverTextRenderer} optionValue="id" fullWidth={true} />
        </ReferenceInput>
      </Box>
      <Box>
        <Typography variant="caption">The service typically takes a few minutes to run.</Typography>
      </Box>
    </Box>
  ),
}

/**
 * Add any default values for scenario fields here.
 *
 * Leave blank for no initial values.
 */
export const scenarioInitialValues = {
  BasicTeamScenario: {
    num_admin_driver: 1,
    num_admin_only: 0,
    num_driver_only: 0,
    product_subscription: 'PREMIUM',
  },
}
